import React, { useState, useEffect } from "react";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import { useTagConfig } from "../../../hooks/useTagConfig";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../../ui/dropdown-menu";
import { Loader2Icon, TagIcon, PlusIcon, XIcon } from "lucide-react";
import { cn } from "../../../lib/utils";
import { notification } from "antd";

interface DatasetTagsProps {
  datasetId: number;
  initialTags?: string[];
  readOnly?: boolean;
  compact?: boolean;
  onTagsChange?: (tags: string[]) => void;
}

const DatasetTags: React.FC<DatasetTagsProps> = ({
  datasetId,
  initialTags,
  readOnly = false,
  compact = false,
  onTagsChange,
}) => {
  const { apiKey } = usePermissions();
  const { validTags, getTagDisplayConfig } = useTagConfig(apiKey);
  const [tags, setTags] = useState<string[]>(initialTags || []);
  const [loading, setLoading] = useState<boolean>(!initialTags);
  const [updating, setUpdating] = useState<boolean>(false);
  const [removingTag, setRemovingTag] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  // Fetch tags if not provided
  useEffect(() => {
    if (initialTags) {
      setTags(initialTags);
      return;
    }

    const subscription = DatasetService.getDatasetTags(datasetId, setLoading, apiKey).subscribe(
      (response: any) => {
        const fetchedTags = response.tags || [];
        setTags(fetchedTags);
      },
      (error: any) => {
        console.error("Error fetching dataset tags:", error);
      }
    );

    return () => subscription.unsubscribe();
  }, [datasetId, initialTags, apiKey]);

  const handleToggleTag = (tag: string) => {
    const newTags = tags.includes(tag)
      ? tags.filter(t => t !== tag)
      : [...tags, tag];

    setUpdating(true);
    DatasetService.setDatasetTags(datasetId, newTags, () => {}, apiKey).subscribe(
      () => {
        setTags(newTags);
        setUpdating(false);
        onTagsChange?.(newTags);
      },
      (error: any) => {
        console.error("Error updating tags:", error);
        notification.error({
          message: "Error",
          description: "Failed to update tags",
        });
        setUpdating(false);
      }
    );
  };

  const handleRemoveTag = (tag: string) => {
    const newTags = tags.filter(t => t !== tag);

    setUpdating(true);
    setRemovingTag(tag);
    DatasetService.removeDatasetTag(datasetId, tag, () => {}, apiKey).subscribe(
      () => {
        setTags(newTags);
        setUpdating(false);
        setRemovingTag(null);
        onTagsChange?.(newTags);
      },
      (error: any) => {
        console.error("Error removing tag:", error);
        notification.error({
          message: "Error",
          description: "Failed to remove tag",
        });
        setUpdating(false);
        setRemovingTag(null);
      }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2Icon className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading tags...</span>
      </div>
    );
  }

  // Compact view - just show badges
  if (compact) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {tags.length === 0 ? (
          <span className="text-xs text-muted-foreground">No tags</span>
        ) : (
          tags.map(tag => {
            const config = getTagDisplayConfig(tag);
            return (
              <Badge
                key={tag}
                variant="outline"
                className={cn("text-xs h-5", config.className)}
              >
                {config.label}
              </Badge>
            );
          })
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Display current tags */}
      {tags.map(tag => {
        const config = getTagDisplayConfig(tag);
        const isRemoving = removingTag === tag;
        return (
          <Badge
            key={tag}
            variant="outline"
            className={cn(
              "text-xs h-6 gap-1 transition-colors",
              isRemoving
                ? "border-gray-300 text-gray-400 bg-gray-100 dark:border-gray-600 dark:text-gray-500 dark:bg-gray-800"
                : config.className,
              !readOnly && "pr-1"
            )}
          >
            {config.label}
            {!readOnly && (
              <button
                onClick={() => handleRemoveTag(tag)}
                disabled={updating}
                className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 transition-colors"
              >
                {isRemoving ? (
                  <Loader2Icon className="w-3 h-3 animate-spin" />
                ) : (
                  <XIcon className="w-3 h-3" />
                )}
              </button>
            )}
          </Badge>
        );
      })}

      {/* Add tag dropdown */}
      {!readOnly && (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              disabled={updating}
            >
              {updating ? (
                <Loader2Icon className="w-3 h-3 animate-spin" />
              ) : (
                <PlusIcon className="w-3 h-3" />
              )}
              Add Tag
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel className="flex items-center gap-2">
              <TagIcon className="w-4 h-4" />
              Dataset Tags
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {validTags.map(tag => {
              const config = getTagDisplayConfig(tag);
              const isSelected = tags.includes(tag);
              return (
                <DropdownMenuCheckboxItem
                  key={tag}
                  checked={isSelected}
                  onCheckedChange={() => handleToggleTag(tag)}
                  disabled={updating}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className={cn("font-medium", isSelected && "text-foreground")}>
                      {config.label}
                    </span>
                    {config.description && (
                      <span className="text-xs text-muted-foreground">
                        {config.description}
                      </span>
                    )}
                  </div>
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Empty state */}
      {tags.length === 0 && readOnly && (
        <span className="text-sm text-muted-foreground">No tags</span>
      )}
    </div>
  );
};

export default DatasetTags;
